import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  MessageSquare,
  Loader2,
  Sparkle,
  MoreVertical,
  Trash2,
  Edit,
  ArrowDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { researchAPI } from '../../services/api/researchApi';
import { MessageRequest, LiteratureOptions } from '../../schemas/chat';
import { toast } from 'sonner';
import { useModel } from '../../contexts/ModelContext';
import { useLiterature } from '../../contexts/LiteratureContext';
import { useKnowledgeBase } from '../../contexts/KnowledgeBaseContext';
import EnhancedChatInput from '../EnhancedChatInput';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import TurnView from '../chat/TurnView';
import { Turn, TurnContent } from '../../types/conversation';

interface FileMetadata {
  file_id: string;
  file_name: string;
  file_type: string;
}

interface ChatPanelProps {
  selectedFileIds: string[];
  files: Array<{
    id: string;
    name: string;
  }>;
  knowledgeBaseId?: string;
  knowledgeBaseName?: string;
  presetPrompt?: string;
  presetPromptKey?: number;
  loadChatId?: string;
  resetChat?: boolean;
  onChatLoaded?: (chatId: string) => void;
  onRemoveSelectedFile?: (fileId: string) => void;
  onPresetPromptSet?: () => void;
  onChatCreated?: (chatId: string, title: string) => void;
  onChatTitleUpdated?: (chatId: string, newTitle: string) => void;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
  selectedFileIds,
  files,
  knowledgeBaseId,
  knowledgeBaseName,
  presetPrompt: propPresetPrompt,
  presetPromptKey,
  loadChatId,
  resetChat,
  onChatLoaded,
  onRemoveSelectedFile,
  onPresetPromptSet,
  onChatCreated,
  onChatTitleUpdated,
}) => {
  const { t } = useTranslation();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [autoScrollPaused, setAutoScrollPaused] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [turnLikes, setTurnLikes] = useState<Record<string, 'like' | 'dislike' | null>>({});
  const [chatId, setChatId] = useState<string | null>(null);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const [chatTitle, setChatTitle] = useState<string>('');
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [newChatTitle, setNewChatTitle] = useState('');
  const [presetPrompt, setPresetPrompt] = useState<string>('');
  const [suggestedActions, setSuggestedActions] = useState<string[]>([]);
  const [suggestedActionsLoading] = useState(false);
  const [currentTurnAttachments, setCurrentTurnAttachments] = useState<FileMetadata[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatMenuRef = useRef<HTMLDivElement>(null);

  // Use contexts for EnhancedChatInput
  const { models, selectedModel, setSelectedModel } = useModel();
  const { state: literatureState, setSelectedSources, setOptions } = useLiterature();
  const { state: kbState } = useKnowledgeBase();

  const selectedFiles = files.filter(f => selectedFileIds.includes(f.id));

  const isStreaming = turns.some(turn => turn.status === 'streaming');

  // Scroll control
  const scrollToBottom = useCallback((smooth = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    });
  }, []);

  const checkIfNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const { scrollTop, clientHeight, scrollHeight } = container;
    const threshold = 150;
    return scrollTop + clientHeight >= scrollHeight - threshold;
  }, []);

  // Auto-scroll during streaming
  useEffect(() => {
    if (!isStreaming || autoScrollPaused) return;

    const intervalId = setInterval(() => {
      scrollToBottom(false);
    }, 100);

    return () => clearInterval(intervalId);
  }, [isStreaming, autoScrollPaused, scrollToBottom]);

  // Monitor user scrolling
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let lastScrollTop = container.scrollTop;

    const handleScroll = () => {
      const currentScrollTop = container.scrollTop;
      const isNearBottom = checkIfNearBottom();
      const isScrollingUp = currentScrollTop < lastScrollTop;

      if (isStreaming && isScrollingUp && !isNearBottom && !autoScrollPaused) {
        setAutoScrollPaused(true);
        setShowScrollButton(true);
      }

      if (isNearBottom && autoScrollPaused) {
        setAutoScrollPaused(false);
        setShowScrollButton(false);
      }

      lastScrollTop = currentScrollTop;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [checkIfNearBottom, isStreaming, autoScrollPaused]);

  // Scroll to bottom when new message arrives
  useEffect(() => {
    if (turns.length > 0 && !isStreaming) {
      scrollToBottom(true);
    }
  }, [turns.length, isStreaming, scrollToBottom]);

  // Reset scroll state when streaming stops
  useEffect(() => {
    if (!isStreaming) {
      setAutoScrollPaused(false);
      setShowScrollButton(false);
    }
  }, [isStreaming]);

  // Handle preset prompt changes from parent
  useEffect(() => {
    if (propPresetPrompt) {
      setPresetPrompt(propPresetPrompt);
      // Notify parent that preset has been set, so it can clear its state
      onPresetPromptSet?.();
    }
  }, [propPresetPrompt, onPresetPromptSet]);

  // Reset chat when resetChat prop changes to true
  useEffect(() => {
    if (resetChat) {
      setTurns([]);
      setChatId(null);
      setChatTitle('');
      setPresetPrompt('');
      setCurrentTurnAttachments([]);
      setSuggestedActions([]);
    }
  }, [resetChat]);

  // Convert API messages to Turn format
  const convertMessagesToTurns = useCallback((messages: any[]): Turn[] => {
    const turns: Turn[] = [];
    let currentUserContent: TurnContent | null = null;
    let currentTimestamp = Date.now();

    messages.forEach(msg => {
      if (msg.role === 'user') {
        // Convert backend file objects to FileMetadata format
        // Backend returns: {file_id, file_name, file_type} or {id, original_name, file_type}
        const attachments =
          (msg.files || msg.file_ids)?.map((f: any) => {
            if (typeof f === 'string') {
              return {
                file_id: f,
                file_name: 'File',
                file_type: 'document',
              } as FileMetadata;
            }
            // Handle different file object structures from backend
            const fileId = f.file_id || f.id || '';
            const fileName = f.file_name || f.original_name || f.name || 'File';
            const fileType = f.file_type || 'document';
            return {
              file_id: fileId,
              file_name: fileName,
              file_type: fileType,
            } as FileMetadata;
          }) || undefined;

        // Save user content for the next turn, including files/attachments
        currentUserContent = {
          text: msg.content,
          attachments,
        };
        currentTimestamp = new Date(msg.created_at || Date.now()).getTime();
      } else if (msg.role === 'assistant' && currentUserContent) {
        // Create a turn with user content and assistant response
        const turn: Turn = {
          id: msg.id,
          content: currentUserContent,
          status: msg.is_done ? 'completed' : 'streaming',
          answerText: msg.content || '',
          thinking: msg.thinking_process?.content,
          createdAt: currentTimestamp,
        };
        turns.push(turn);
        currentUserContent = null;
      }
    });

    // Handle case where last message was user (no response yet)
    if (currentUserContent) {
      const turn: Turn = {
        id: `temp-${Date.now()}`,
        content: currentUserContent,
        status: 'created',
        answerText: '',
        createdAt: currentTimestamp,
      };
      turns.push(turn);
    }

    return turns;
  }, []);

  // Load existing chat when loadChatId changes
  useEffect(() => {
    if (loadChatId && loadChatId !== chatId) {
      const loadChat = async () => {
        try {
          setIsLoading(true);
          // Fetch chat details
          const chatResponse = await researchAPI.getResearchChat(loadChatId);
          setChatId(loadChatId);
          setChatTitle(chatResponse.data.title || '');

          // Fetch messages
          const messagesResponse = await researchAPI.getResearchMessages(loadChatId);

          // Convert API messages to Turn format
          const convertedTurns = convertMessagesToTurns(messagesResponse.data.messages);
          setTurns(convertedTurns);

          // Check for incomplete messages and resume them
          const incompleteMessage = messagesResponse.data.messages.find(
            (msg: any) => !msg.is_done && msg.role === 'assistant'
          );
          if (incompleteMessage) {
            // Find the corresponding turn
            const turnIndex = convertedTurns.findIndex(t => t.id === incompleteMessage.id);
            if (turnIndex !== -1) {
              // Resume the incomplete message
              try {
                const response = await researchAPI.resumeMessageStream(
                  loadChatId,
                  incompleteMessage.id
                );
                if (!response.body) {
                  throw new Error('Response body is null');
                }

                const { processStream } = await import('../../lib/stream');
                const reader = response.body.getReader();

                let accumulatedContent = incompleteMessage.content || '';
                let accumulatedThinking = incompleteMessage.thinking_process?.content || '';

                await processStream(
                  reader,
                  // onChunk
                  (chunk: string) => {
                    accumulatedContent += chunk;
                    setTurns(prev =>
                      prev.map((turn, idx) =>
                        idx === turnIndex ? { ...turn, answerText: accumulatedContent } : turn
                      )
                    );
                  },
                  // onThinkingChunk
                  (thinkingChunk: string) => {
                    accumulatedThinking += thinkingChunk;
                    setTurns(prev =>
                      prev.map((turn, idx) =>
                        idx === turnIndex ? { ...turn, thinking: accumulatedThinking } : turn
                      )
                    );
                  },
                  // onComplete
                  (completedMessage: any) => {
                    setTurns(prev =>
                      prev.map((turn, idx) =>
                        idx === turnIndex
                          ? {
                              ...turn,
                              answerText: completedMessage.content || '',
                              thinking: completedMessage.thinking_process?.content,
                              status: 'completed' as const,
                            }
                          : turn
                      )
                    );
                    setIsLoading(false);
                  },
                  // onTitleChunk - update title from backend event
                  (title: string) => {
                    setChatTitle(title);
                    onChatTitleUpdated?.(loadChatId, title);
                  }
                );
              } catch (error) {
                console.error('Failed to resume message:', error);
                setTurns(prev =>
                  prev.map((turn, idx) =>
                    idx === turnIndex
                      ? {
                          ...turn,
                          status: 'failed' as const,
                          error: { message: 'Failed to resume' },
                        }
                      : turn
                  )
                );
              }
            }
          }

          if (onChatLoaded) {
            onChatLoaded(loadChatId);
          }
        } catch (error) {
          console.error('Failed to load chat:', error);
          // Error is handled by global HTTP error handler
        } finally {
          setIsLoading(false);
        }
      };

      loadChat();
    }
  }, [loadChatId, chatId, onChatLoaded, convertMessagesToTurns]);

  // Create chat conversation - called when user sends first message
  const createChatConversation = useCallback(async (): Promise<string | null> => {
    if (!knowledgeBaseId) return null;

    setIsCreatingChat(true);
    try {
      const response = await researchAPI.createKnowledgeBaseChat(
        knowledgeBaseId,
        knowledgeBaseName || `Knowledge Base: ${knowledgeBaseId}`
      );
      const newChatId = response.data.id;
      setChatId(newChatId);
      setTurns([]);
      return newChatId;
    } catch (error) {
      console.error('Failed to create chat:', error);
      // Error is handled by global HTTP error handler (toast notification)
      return null;
    } finally {
      setIsCreatingChat(false);
    }
  }, [knowledgeBaseId, knowledgeBaseName]);

  const handleLike = useCallback((turnId: string, type: 'like' | 'dislike') => {
    setTurnLikes(prev => ({
      ...prev,
      [turnId]: prev[turnId] === type ? null : type,
    }));
  }, []);

  const handleCopy = useCallback((content: string) => {
    navigator.clipboard.writeText(content);
  }, []);

  // Handle delete chat
  const handleDeleteClick = () => {
    setChatMenuOpen(false);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      // Delete the chat from backend if chatId exists
      if (chatId) {
        await researchAPI.deleteResearchChat(chatId);
      }
      // Clear the chat state
      const deletedTitle = chatTitle;
      setTurns([]);
      setChatId(null);
      setTurnLikes({});
      setChatTitle('');
      setShowDeleteDialog(false);
      toast.success(t('chat.deleteConversationSuccess', { title: deletedTitle }));
    } catch (error) {
      console.error('Failed to delete chat:', error);
      // Error is handled by global HTTP error handler (toast notification)
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle rename click
  const handleRenameClick = () => {
    setNewChatTitle(chatTitle);
    setShowRenameDialog(true);
    setChatMenuOpen(false);
  };

  // Handle rename submit
  const handleRenameSubmit = async () => {
    if (!chatId || !newChatTitle.trim()) return;
    try {
      const oldTitle = chatTitle;
      await researchAPI.updateResearchChat(chatId, { title: newChatTitle });
      setChatTitle(newChatTitle);
      setShowRenameDialog(false);
      setNewChatTitle('');
      toast.success(t('chat.renameConversationSuccess', { oldTitle, newTitle: newChatTitle }));
    } catch (error) {
      console.error('Failed to rename chat:', error);
      // Error is handled by global HTTP error handler (toast notification)
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        chatMenuOpen &&
        chatMenuRef.current &&
        !chatMenuRef.current.contains(event.target as Node)
      ) {
        setChatMenuOpen(false);
      }
    };

    if (chatMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [chatMenuOpen]);

  // Handle send from EnhancedChatInput
  const handleSendMessage = async (data: MessageRequest) => {
    if (isLoading || isCreatingChat) return;

    // Set title from first user message if not already set
    if (!chatTitle && data.content) {
      // Truncate to first 50 characters
      const title = data.content.length > 50 ? data.content.substring(0, 50) + '...' : data.content;
      setChatTitle(title);
    }

    // Create chat conversation on first message if it doesn't exist
    let currentChatId = chatId;
    if (!currentChatId) {
      currentChatId = await createChatConversation();
      if (!currentChatId) {
        // Error is handled by global HTTP error handler (toast notification)
        return;
      }

      // Calculate initial title from content
      const initialTitle =
        data.content.trim().length > 50
          ? data.content.trim().substring(0, 50) + '...'
          : data.content.trim();

      // Notify parent about new chat
      onChatCreated?.(currentChatId, initialTitle);
    }

    // Prepare file attachments for current turn from multiple sources
    const attachments: FileMetadata[] = [];

    // Add files uploaded via EnhancedChatInput (data.files)
    if (data.files && data.files.length > 0) {
      const uploadedFilesMetadata: FileMetadata[] = data.files.map(f => ({
        file_id: f.id,
        file_name: f.original_name || f.name || 'Unknown',
        file_type: f.file_type || 'document',
      }));
      attachments.push(...uploadedFilesMetadata);
    }

    // Add files selected from Files page (selectedFileIds)
    if (selectedFileIds.length > 0) {
      const selectedFilesMetadata: FileMetadata[] = selectedFileIds.map(id => {
        const file = selectedFiles.find(f => f.id === id);
        return {
          file_id: id,
          file_name: file?.name || 'Unknown',
          file_type: 'document',
        };
      });
      attachments.push(...selectedFilesMetadata);
    }

    setCurrentTurnAttachments(attachments);

    // Create a new turn
    const newTurnId = Date.now().toString();
    const newTurn: Turn = {
      id: newTurnId,
      content: { text: data.content },
      status: 'streaming',
      answerText: '',
      createdAt: Date.now(),
    };

    setTurns(prev => [...prev, newTurn]);
    setIsLoading(true);

    // Clear selected files after sending (matches ChatPage behavior)
    if (selectedFileIds.length > 0 && onRemoveSelectedFile) {
      for (const fileId of selectedFileIds) {
        onRemoveSelectedFile(fileId);
      }
    }

    try {
      // Prepare message data - should match SendMessageStreamRequest format
      const messageData: {
        content: string;
        model_id?: string;
        knowledge_base_ids?: string[];
        files?: string[];
        literature_sources?: string[];
        literature_options?: LiteratureOptions;
      } = {
        content: data.content,
      };

      // Include model_id if provided
      if (data.model_id) {
        messageData.model_id = data.model_id;
      }

      // Include knowledge_base_ids if provided (from EnhancedChatInput)
      if (data.knowledge_base_ids && data.knowledge_base_ids.length > 0) {
        messageData.knowledge_base_ids = data.knowledge_base_ids;
      }

      // Collect all file IDs from multiple sources:
      // 1. Files uploaded via EnhancedChatInput (data.files)
      // 2. Files selected from Files page (selectedFileIds)
      const allFileIds: string[] = [];

      // Add uploaded file IDs from EnhancedChatInput
      if (data.files && data.files.length > 0) {
        const uploadedFileIds = data.files.map(f => f.id);
        allFileIds.push(...uploadedFileIds);
      }

      // Add selected file IDs from Files page
      if (selectedFileIds.length > 0) {
        allFileIds.push(...selectedFileIds);
      }

      // Include all file IDs in the message
      if (allFileIds.length > 0) {
        messageData.files = allFileIds;
      }

      // Include literature sources and options if provided
      if (data.literature_sources && data.literature_sources.length > 0) {
        messageData.literature_sources = data.literature_sources;
        messageData.literature_options = data.literature_options || undefined;
      }

      // Stream the message using the research API
      const response = await researchAPI.streamKnowledgeBaseMessage(currentChatId, messageData);

      if (!response.body) {
        throw new Error('Response body is null');
      }

      // Process the streaming response
      const { processStream } = await import('../../lib/stream');
      const reader = response.body.getReader();

      let accumulatedContent = '';
      let accumulatedThinking = '';

      await processStream(
        reader,
        // onChunk
        (chunk: string) => {
          accumulatedContent += chunk;
          setTurns(prev =>
            prev.map(turn =>
              turn.id === newTurnId ? { ...turn, answerText: accumulatedContent } : turn
            )
          );
        },
        // onThinkingChunk
        (thinkingChunk: string) => {
          accumulatedThinking += thinkingChunk;
          setTurns(prev =>
            prev.map(turn =>
              turn.id === newTurnId ? { ...turn, thinking: accumulatedThinking } : turn
            )
          );
        },
        // onComplete
        (completedMessage: any) => {
          setTurns(prev =>
            prev.map(turn =>
              turn.id === newTurnId
                ? {
                    ...turn,
                    answerText: completedMessage.content || '',
                    thinking: completedMessage.thinking_process?.content,
                    status: 'completed' as const,
                  }
                : turn
            )
          );
          setIsLoading(false);
          setCurrentTurnAttachments([]);
        },
        // onTitleChunk - update title from backend event
        (title: string) => {
          setChatTitle(title);
          onChatTitleUpdated?.(currentChatId, title);
        }
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setTurns(prev =>
        prev.map(turn =>
          turn.id === newTurnId
            ? {
                ...turn,
                status: 'failed' as const,
                error: {
                  message: error instanceof Error ? error.message : 'Failed to send message',
                },
              }
            : turn
        )
      );
      setIsLoading(false);
      setCurrentTurnAttachments([]);
      // Error is handled by global HTTP error handler (toast notification)
    }
  };

  // Handle suggested action
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSuggestedAction = useCallback(async (action: string) => {
    // Send the suggested action as a new message
    await handleSendMessage({ content: action });
  }, []);

  // Welcome message
  const renderWelcomeMessage = () => (
    <div className='flex items-center justify-center h-full'>
      <div className='text-center max-w-md px-6'>
        {isCreatingChat ? (
          <>
            <div className='w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6'>
              <Loader2 className='text-blue-500 animate-spin' size={40} />
            </div>
            <h3 className='text-xl font-semibold text-gray-900 mb-2'>
              {t('knowledge.chat.initializing', 'Initializing chat...')}
            </h3>
          </>
        ) : (
          <>
            <div className='w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6'>
              <MessageSquare className='text-blue-500' size={40} />
            </div>
            <h3 className='text-xl font-semibold text-gray-900 mb-2'>
              {t('knowledge.chat.welcome', 'Chat with your knowledge')}
            </h3>
            <p className='text-gray-600 mb-6'>
              {selectedFiles.length > 0
                ? t(
                    'knowledge.chat.welcomeMessageSelected',
                    'Ask questions about your selected files and get intelligent answers.'
                  )
                : t(
                    'knowledge.chat.welcomeMessageAll',
                    'Ask questions about your entire knowledge base and get intelligent answers.'
                  )}
            </p>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className='flex flex-col h-full bg-gray-50 relative'>
      {/* Header with 3-dot menu */}
      {(chatId || turns.length > 0) && (
        <div className='flex-shrink-0 h-[45px] border-b border-gray-200 px-4 flex items-center justify-between'>
          <h3 className='font-semibold text-gray-900 text-sm truncate flex-1'>
            {chatTitle || t('knowledge.chat.welcome', 'Chat')}
          </h3>
          <div className='flex items-center gap-2'>
            {/* New conversation button - mobile only */}
            <button
              onClick={() => {
                setTurns([]);
                setChatId(null);
                setChatTitle('');
                setPresetPrompt('');
                setCurrentTurnAttachments([]);
                setSuggestedActions([]);
                onChatCreated?.('', '');
              }}
              className='md:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-colors'
              aria-label={t('chat.newConversation')}
              title={t('chat.newConversation')}
            >
              <MessageSquare size={16} className='text-gray-600' />
            </button>

            <div className='relative' ref={chatMenuRef}>
              <button
                onClick={() => setChatMenuOpen(!chatMenuOpen)}
                className='p-1.5 hover:bg-gray-100 rounded-lg transition-colors'
                aria-label='Chat options'
              >
                <MoreVertical size={16} className='text-gray-500' />
              </button>

              {/* Dropdown Menu */}
              {chatMenuOpen && (
                <div className='absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px] z-10'>
                  <button
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                    onClick={handleRenameClick}
                  >
                    <Edit size={14} />
                    {t('chat.renameConversation')}
                  </button>
                  <div className='border-t border-gray-100 my-1' />
                  <button
                    className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-red-600'
                    onClick={handleDeleteClick}
                  >
                    <Trash2 size={14} />
                    {t('chat.deleteConversation')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div ref={messagesContainerRef} className='flex-1 overflow-y-auto scrollbar-ui'>
        <div className='max-w-4xl mx-auto px-4 py-6'>
          {turns.length === 0 ? (
            renderWelcomeMessage()
          ) : (
            <div className='space-y-6'>
              {turns.map((turn, index) => {
                const isLastTurn = index === turns.length - 1;

                // Get attachments from turn content or currentTurnAttachments for the last turn
                // turn.content.attachments is already FileMetadata[] format after conversion
                let turnAttachments: FileMetadata[] = turn.content.attachments || [];

                if (isLastTurn && currentTurnAttachments.length > 0) {
                  // Use currentTurnAttachments for the last turn (newly created, not from backend)
                  turnAttachments = currentTurnAttachments;
                }

                return (
                  <TurnView
                    key={turn.id}
                    turn={turn}
                    streaming={turn.status === 'streaming'}
                    onCopy={handleCopy}
                    onLike={type => handleLike(turn.id, type)}
                    likeStatus={turnLikes[turn.id] || null}
                    showSuggestions={isLastTurn && turn.status === 'completed'}
                    suggestedActions={suggestedActions}
                    suggestedActionsLoading={suggestedActionsLoading}
                    onSuggestedAction={handleSuggestedAction}
                    attachments={turnAttachments}
                  />
                );
              })}
            </div>
          )}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          onClick={() => {
            scrollToBottom(true);
            setAutoScrollPaused(false);
            setShowScrollButton(false);
          }}
          className='absolute bottom-36 right-4 bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-md hover:shadow-lg transition-all duration-200 z-50'
          title='Scroll to bottom'
        >
          <ArrowDown size={20} />
        </button>
      )}

      {/* Input Area */}
      <div className='flex-shrink-0 w-full bg-gray-50 border-t border-gray-200'>
        <div className='max-w-4xl mx-auto px-2 sm:px-4 py-2 sm:py-4'>
          <EnhancedChatInput
            key={presetPromptKey}
            initialValue={presetPrompt}
            onSendMessage={handleSendMessage}
            placeholder={
              selectedFiles.length > 0
                ? t('knowledge.chat.inputWithFiles', 'Ask about your selected files...')
                : t('knowledge.chat.inputAllFiles', 'Ask about anything in your knowledge base...')
            }
            disabled={isLoading || isCreatingChat}
            selectedModel={selectedModel}
            models={models}
            onSelectModel={setSelectedModel}
            kbState={kbState}
            literatureSources={literatureState.selectedSources}
            onSelectLiterature={setSelectedSources}
            literatureOptions={literatureState.options}
            onSelectLiteratureOptions={setOptions}
            hideKnowledgeBaseSelector={true}
            currentKnowledgeBaseId={knowledgeBaseId}
            selectedFileIds={selectedFileIds}
            selectedFiles={selectedFiles}
            onRemoveSelectedFile={onRemoveSelectedFile}
          />
          {selectedFiles.length > 0 ? (
            <div className='mt-2 text-xs text-gray-500 flex items-center gap-1'>
              <Sparkle size={12} />
              {t('knowledge.chat.contextFiles', 'Using {{count}} file(s) from your selection', {
                count: selectedFiles.length,
              })}
            </div>
          ) : (
            <div className='mt-2 text-xs text-gray-500 flex items-center gap-1'>
              <Sparkle size={12} />
              {t('knowledge.chat.contextAllFiles', 'Using all files in knowledge base')}
            </div>
          )}
          {/* AI Disclaimer */}
          <p className='text-xs text-gray-400 text-center mt-2'>
            {t('chat.aiDisclaimer', 'Answers are generated by AI and are for reference only.')}
          </p>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title={t('chat.deleteConversation')}
        message={t('chat.confirmDeleteConversation')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant='danger'
        loading={isDeleting}
        data-testid='delete-kb-chat-dialog'
      />

      {/* Rename Dialog */}
      {showRenameDialog && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50'
          onClick={() => {
            setShowRenameDialog(false);
            setNewChatTitle('');
          }}
        >
          <div
            className='relative bg-white rounded-lg shadow-xl w-full max-w-md p-6'
            onClick={e => e.stopPropagation()}
          >
            <h2 className='text-xl font-semibold text-gray-900 mb-4'>
              {t('chat.renameConversation')}
            </h2>
            <input
              type='text'
              value={newChatTitle}
              onChange={e => setNewChatTitle(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleRenameSubmit();
                } else if (e.key === 'Escape') {
                  setShowRenameDialog(false);
                  setNewChatTitle('');
                }
              }}
              className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
              autoFocus
              ref={input => {
                if (input) input.focus();
              }}
            />
            <div className='flex justify-end space-x-3 mt-6'>
              <button
                onClick={() => {
                  setShowRenameDialog(false);
                  setNewChatTitle('');
                }}
                className='px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleRenameSubmit}
                disabled={!newChatTitle.trim()}
                className='px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
